import {
  Settings, Check, MapPin, Calendar as CalendarIcon,
  Users, Wallet, Globe, Plane,
} from 'lucide-react';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import { PREFERENCES } from './tripConstants';

export const PreferencesStep = ({ formData, updateFormData, toggleArrayItem }) => (
  <div className="space-y-6">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-ocean to-gold flex items-center justify-center text-white shadow-lg">
        <Settings className="w-6 h-6" />
      </div>
      <div>
        <h3 className="text-xl font-semibold text-ocean">Any special preferences?</h3>
        <p className="text-ocean/70 text-sm">Optional - select any that apply</p>
      </div>
    </div>
    <div className="flex flex-wrap gap-3">
      {PREFERENCES.map((pref) => (
        <button key={pref.value}
          onClick={() => toggleArrayItem('preferences', pref.value)}
          className={cn('inline-flex items-center gap-2 px-5 py-3 rounded-full border-2 transition-all duration-300',
            formData.preferences.includes(pref.value)
              ? 'border-gold bg-gradient-to-r from-gold to-gold-light text-ocean shadow-lg scale-105'
              : 'border-ocean/20 bg-white/50 hover:border-gold/50 hover:shadow-md text-ocean backdrop-blur-sm')}
          data-testid={`pref-${pref.value}`}>
          <span className="text-lg">{pref.icon}</span>
          {pref.label}
          {formData.preferences.includes(pref.value) && <Check className="w-4 h-4 ml-1" />}
        </button>
      ))}
    </div>

    <div className="bg-gradient-to-br from-sky/10 to-ocean/10 p-5 rounded-xl border border-ocean/20 backdrop-blur-sm">
      <Label className="text-ocean font-medium flex items-center gap-2 mb-2">
        <Settings className="w-4 h-4 text-gold" /> Additional Preferences
        <span className="text-xs text-ocean/50 font-normal">(optional)</span>
      </Label>
      <textarea
        value={formData.custom_preferences}
        onChange={(e) => updateFormData('custom_preferences', e.target.value)}
        placeholder="Tell us any other preferences, dietary requirements, mobility needs, or special requests for your trip..."
        className="w-full h-24 p-3 rounded-lg border border-ocean/20 bg-white/70 text-ocean text-sm placeholder:text-ocean/40 focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold resize-none"
        data-testid="custom-preferences-input"
      />
    </div>

    <div className="mt-8 p-6 bg-gradient-to-br from-ocean/15 to-gold/15 rounded-xl border border-ocean/30 backdrop-blur-sm">
      <h4 className="font-semibold text-ocean mb-4 flex items-center gap-2">
        <Plane className="w-5 h-5 text-gold" /> Trip Summary
      </h4>
      <div className="grid md:grid-cols-2 gap-4 text-sm">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-gold" />
          <span className="text-ocean/70">Destination:</span>
          <span className="font-medium text-ocean">
            {formData.destination_cities.length > 0 
              ? `${formData.destination_cities.join(' \u2192 ')}${formData.destination_countries.length > 1 ? ` (${formData.destination_countries.join(', ')})` : `, ${formData.destination_country}`}`
              : `${formData.destination_city}, ${formData.destination_country}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-4 h-4 text-ocean" />
          <span className="text-ocean/70">Dates:</span>
          <span className="font-medium font-mono text-ocean">{formData.start_date} \u2192 {formData.end_date}</span>
        </div>
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-gold" />
          <span className="text-ocean/70">Travelers:</span>
          <span className="font-medium text-ocean">{formData.traveler_type} - {formData.adults} adults{formData.kids > 0 ? `, ${formData.kids} kids` : ''}</span>
        </div>
        <div className="flex items-center gap-2">
          <Wallet className="w-4 h-4 text-ocean" />
          <span className="text-ocean/70">Budget:</span>
          <span className="font-medium capitalize text-ocean">{formData.budget}</span>
        </div>
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-gold" />
          <span className="text-ocean/70">Nationality:</span>
          <span className="font-medium text-ocean">{formData.nationality}</span>
        </div>
        {formData.custom_preferences && (
          <div className="flex items-start gap-2 md:col-span-2">
            <Settings className="w-4 h-4 text-gold mt-0.5" />
            <span className="text-ocean/70">Notes:</span>
            <span className="font-medium text-ocean text-xs">{formData.custom_preferences}</span>
          </div>
        )}
      </div>
    </div>
  </div>
);
